import { useRef, useState, useEffect } from "react";

/**
 * Before/After image slider with draggable vertical divider.
 * Props: beforeSrc, afterSrc, beforeLabel, afterLabel
 */
export default function BeforeAfterSlider({ beforeSrc, afterSrc, beforeLabel = "Prima", afterLabel = "Dopo" }) {
  const [pos, setPos] = useState(50);
  const containerRef = useRef();
  const dragging = useRef(false);

  useEffect(() => {
    const up = () => (dragging.current = false);
    const move = (e) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const p = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setPos(p);
    };
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl bg-stone-100 select-none"
      data-testid="before-after-slider"
    >
      <img src={afterSrc} alt="Dopo" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img src={beforeSrc} alt="Prima" className="w-full h-full object-cover" draggable={false} />
      </div>
      {/* Labels */}
      <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-white/90 text-stone-900 px-2 py-1 rounded-full">
        {beforeLabel}
      </span>
      <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-stone-900/90 text-white px-2 py-1 rounded-full">
        {afterLabel}
      </span>
      {/* Divider + handle */}
      <div
        className="absolute inset-y-0 w-0.5 bg-white shadow-lg"
        style={{ left: `${pos}%` }}
      >
        <button
          onMouseDown={() => (dragging.current = true)}
          onTouchStart={() => (dragging.current = true)}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-xl flex items-center justify-center backdrop-blur cursor-ew-resize border border-stone-200 hover:scale-110 transition"
          data-testid="slider-handle"
          aria-label="Deslizar"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M8 6l-4 6 4 6M16 6l4 6-4 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
